import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { STAGES, STAGE_DOT } from "@/lib/constants";
import { api, formatErr } from "@/lib/api";
import { LeadCard } from "./LeadCard";
import { LostReasonDialog } from "./LostReasonDialog";

export function useStageMove() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(null);
  const m = useMutation({
    mutationFn: ({ id, stage, lost_reason }) => api.patch(`/leads/${id}/stage`, { stage, lost_reason }),
    onSuccess: (_, v) => {
      toast.success(`Moved to ${v.stage}`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["lead", v.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(formatErr(e)),
  });
  const move = (lead, stage) => {
    if (stage === lead.stage) return;
    if (stage === "Closed Lost") setPending({ lead, stage });
    else m.mutate({ id: lead.id, stage });
  };
  const dialog = (
    <LostReasonDialog
      open={!!pending}
      onCancel={() => setPending(null)}
      onConfirm={(reason) => {
        m.mutate({ id: pending.lead.id, stage: "Closed Lost", lost_reason: reason });
        setPending(null);
      }}
    />
  );
  return { move, dialog };
}

export default function LeadKanban({ leads, userMap }) {
  const { move, dialog } = useStageMove();
  const [over, setOver] = useState(null);
  const byId = Object.fromEntries(leads.map((l) => [l.id, l]));
  return (
    <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10" data-testid="leads-kanban">
      <div className="flex gap-4" style={{ minWidth: STAGES.length * 290 }}>
        {STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage);
          return (
            <div
              key={stage}
              data-testid={`kanban-column-${stage.replace(/\s+/g, "-").toLowerCase()}`}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(stage);
              }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                const lead = byId[e.dataTransfer.getData("text/plain")];
                if (lead) move(lead, stage);
              }}
              className={cn("w-[280px] shrink-0 rounded-2xl border bg-secondary/40 p-3 transition-colors", over === stage && "border-primary bg-primary/5")}
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span className={cn("h-2 w-2 rounded-full", STAGE_DOT[stage])} />
                  {stage}
                </span>
                <span className="num rounded-full bg-card px-2 text-xs">{items.length}</span>
              </div>
              <div className="space-y-2.5 min-h-[80px]">
                {items.map((l) => (
                  <div key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)}>
                    <LeadCard lead={l} owner={userMap[l.assigned_to]} onMove={move} compact />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {dialog}
    </div>
  );
}
